import React, { useEffect, useState } from "react";
import { Fingerprint, Package, Layers, Boxes, ShieldCheck, FlaskConical, ArrowUpRight } from "lucide-react";
import { HoloCard, accentColor } from "@/components/Holo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { abbrevNum, fitTextSize } from "@/lib/format";
import StatDetail from "@/pages/dashboard/StatDetail";

const LAUNCH = [
  { key: "identity", icon: Fingerprint, label: "Identity Matrix", accent: "pink" },
  { key: "brand", icon: Package, label: "Brand Core", accent: "turquoise" },
  { key: "scene", icon: Layers, label: "Scene Memory", accent: "green" },
  { key: "worldforge", icon: Boxes, label: "World Forge", accent: "purple" },
  { key: "ai_lab", icon: FlaskConical, label: "AI Laboratory", accent: "yellow" },
  { key: "credit_shield", icon: ShieldCheck, label: "Credit Shield", accent: "turquoise" },
];

export default function Overview({ setActive }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [panel, setPanel] = useState(null);
  useEffect(() => { api.get("/analytics").then(({ data }) => setD(data)).catch((e) => console.error("analytics load failed:", e)); }, []);

  const vaultTotal = d ? d.characters + d.products + d.scenes : 0;
  const STATS = [
    { key: "credits", label: "Credits", value: user?.credits ?? 0, accent: "turquoise", needsD: false },
    { key: "renders", label: "Renders", value: d?.total_generations ?? 0, accent: "pink", needsD: true },
    { key: "vault", label: "Vault Entries", value: vaultTotal, accent: "green", needsD: true },
    { key: "campaigns", label: "Campaigns", value: d?.campaigns ?? 0, accent: "purple", needsD: true },
  ];

  return (
    <div data-testid="module-overview">
      <p className="font-mono-tech text-neon-turquoise text-[11px] uppercase tracking-[0.3em]">// System Online</p>
      <h1 className="font-display font-bold text-2xl sm:text-3xl mt-2">Welcome back, {user?.name?.split(" ")[0] || "Creator"}.</h1>
      <p className="font-mono-tech text-white/40 text-sm mt-1">Your consistency engine is standing by.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
        {STATS.map((s) => {
          const c = accentColor(s.accent);
          const isLoading = s.needsD && !d;
          const disp = abbrevNum(s.value);
          return (
            <button key={s.key} onClick={() => setPanel(s.key)} data-testid={`overview-card-${s.key}`}
              className="group text-center cursor-pointer transition-transform duration-200 hover:scale-[1.03] active:scale-95 focus:outline-none">
              <HoloCard accent={s.accent} className="p-5 h-full flex flex-col items-center justify-center min-h-[7rem]" testid={`overview-stat-${s.label}`}>
                {isLoading ? (
                  <div className="h-9 w-16 bg-white/10 animate-pulse rounded" data-testid={`overview-loading-${s.key}`} />
                ) : (
                  <div className={`w-full font-display font-black ${fitTextSize(disp)} leading-none tabular-nums text-center`} style={{ color: c.hex }} title={Number(s.value).toLocaleString()}>
                    {disp}
                  </div>
                )}
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40 mt-2 flex items-center justify-center gap-1">
                  {s.label} <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: c.hex }} />
                </div>
              </HoloCard>
            </button>
          );
        })}
      </div>

      <h2 className="font-mono-tech text-white/50 text-xs uppercase tracking-widest mt-12 mb-4">// Launch Module</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {LAUNCH.map((m) => {
          const c = accentColor(m.accent);
          return (
            <button key={m.key} onClick={() => setActive(m.key)} data-testid={`launch-${m.key}`} className="text-left transition-transform duration-200 hover:scale-[1.02]">
              <HoloCard accent={m.accent} className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center border border-white/15" style={{ color: c.hex }}><m.icon size={20} strokeWidth={1.5} /></div>
                  <span className="font-display font-bold">{m.label}</span>
                </div>
                <ArrowUpRight size={18} className="text-white/30" />
              </HoloCard>
            </button>
          );
        })}
      </div>

      {panel && <StatDetail cardKey={panel} user={user} setActive={setActive} onClose={() => setPanel(null)} />}
    </div>
  );
}
